import { AuthData } from 'lib-shared-auth';
import { AsyncStatus } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { AppLogInType } from 'app/types/app.types';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { authResolverErrorFallThroughValue } from '../../useAuthDataResolver.constants';

/** The result of the trigger callback. */
export type UseAuthSearchParamsTriggerResult = {
  /** The type of the login. */
  readonly type: Extract<AppLogInType, 'searchParams'>;
  /** The async status. */
  readonly asyncStatus: Extract<AsyncStatus, 'fulfilled'>;
  /** The auth data. */
  readonly authData: Pick<AuthData, 'refresh_token'>;
};

/** Represents the trigger for getting the auth data. */
type Trigger = () => Promise<UseAuthSearchParamsTriggerResult>;

/** A hook that resolves the auth data from the app token params. */
export const useAuthSearchParams = (): Trigger => {
  const {
    params: { refreshToken },
    unsetParam,
  } = useAppSearchParams('/');

  /**
   * Gets the auth data through the login api endpoint for exchanging the login
   * token and organization id to the auth data.
   */
  const getAuthDataFromSearchParam = useCallback<Trigger>(() => {
    if (!refreshToken) {
      return Promise.reject(authResolverErrorFallThroughValue);
    }

    unsetParam('refreshToken');

    return Promise.resolve({
      type: 'searchParams',
      asyncStatus: 'fulfilled',
      authData: { refresh_token: refreshToken },
    });
  }, [refreshToken, unsetParam]);

  /** Triggers the retrieval of the auth data in a promise. */
  const trigger = useCallback<Trigger>(() => {
    return getAuthDataFromSearchParam();
  }, [getAuthDataFromSearchParam]);

  return trigger;
};
